import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {FormattedDate, FormattedMessage, FormattedNumber} from 'react-intl';
import CurrencyNumber from '../../cmp/CurrencyNumber';
import LazyRangePicker from '../../cmp/lazy/LazyRangePicker';
import dayjs from 'dayjs';
import {connect, useSelector} from 'react-redux';
import useInputState from '../../cmp/hooks/useInputState';
import {get} from '../../../api';
import Big from 'big.js';
import Checkbox from '../../cmp/form/Checkbox';
import uniq from 'lodash.uniq';
import Skeleton from 'react-loading-skeleton';
import BackButton from '../../cmp/BackButton';
import FormattedOption from '../../cmp/form/FormattedOption';
import BootStrapTable from '../../cmp/BootStrapTable';
import {ExternalLinkIcon} from '../../../icons/Icons';
import {buildRefLink} from '@goaffpro/aff-client-commons/functions';
import Input from '../../cmp/form/Input';
import useDebounce from '../../cmp/hooks/useDebounce';
import uniqby from 'lodash.uniqby';

function TeamExplorer({num_levels, affiliate_id}) {
    const [dates, setDates] = useState([dayjs().startOf('month').toDate(), new Date()])
    const [affiliates, setAffiliates] = useState([]);
    const [level, setLevel] = useInputState(1)
    const [loading, setLoading] = useState(false);
    useEffect(()=>{
        setLoading(true);
        get('/mlm/subAffiliateSales', {
            created_at_max: dates && dates.length > 1 ? dates[1] : null,
            created_at_min: dates && dates.length > 0 ? dates[0] : null,
            level: level
        }).then((data)=>{
            setLoading(false)
            setAffiliates(data.salesSummary)
        })
    },[dates, level])

    return (<>
            <BackButton/>
            <h1><FormattedMessage id={"Network explorer"}/></h1>
            <div className="p-2 p-md-4 bg-light rounded">
                <div className="row">
                    <div className="col-md-6 mb-2 mb-md-0">
                        <select className="form-control" value={level} onChange={setLevel}>
                            <FormattedOption value={""}>Entire team</FormattedOption>
                            {
                                [...Array(num_levels)].map((item, index)=>{
                                    const l = Number(index) + 1
                                    return <FormattedMessage id={"Level"} key={String(l)}>
                                        {(message) => <option value={l}>{message} {l}</option>}
                                    </FormattedMessage>
                                })
                            }
                        </select>
                    </div>
                    <div className="col-md-6">
                        <LazyRangePicker onChange={setDates} startDate={dates[0]} endDate={dates[1]}/>
                    </div>
                </div>
            </div>

            <div className="my-4">
                {
                    loading ? <Skeleton count={4}/> :
                        <RenderStatsCard affiliates={affiliates}/>
                }
            </div>
            <div className="my-4">
                {
                    loading ? <Skeleton count={20}/> :
                        <RenderAffiliateSales
                            startDate={dates[0]}
                            endDate={dates[1]}
                            affiliate_id={affiliate_id} affiliates={affiliates}/>
                }
            </div>

        </>
    );
}

const colorsArray = [
    "#1abc9c",
    "#2ecc71",
    "#3498db",
    "#9b59b6",
    "#34495e",
    "#16a085",
    "#27ae60",
    "#2980b9",
    "#8e44ad",
    "#2c3e50"
]

function RenderStatsCard({ affiliates }){
    if(!affiliates) return null;
    const levels = uniq(affiliates.map(({level})=>level));
    const revenue = Number(affiliates.reduce((acc, cur)=> acc.plus(cur.revenue || 0), Big(0)).toFixed(2))
    const numOrders = Number(affiliates.reduce((acc, cur)=> acc.plus(cur.numOrders || 0), Big(0)).toFixed(2))
    const earnings = Number(affiliates.reduce((acc, cur)=>acc.plus(cur.earnings || 0), Big(0)).toFixed(2))
    return <>
        <>
            <h4><FormattedMessage id={"Summary"}/></h4>
            <div className="row">
                <CardItem label={"Number of sales"} value={numOrders}/>
                <CardItem label={"Total sale volume"} value={<CurrencyNumber value={revenue} />}/>
                <div className="d-none">
                    <CardItem label={"Total earnings"} value={<CurrencyNumber value={earnings} />}/>
                </div>
            </div>
        </>
        {
            levels.length > 1 ? <ul className={"list-group mt-4"}>{
                levels.map((item) => <RenderLevelSummary key={String(item)} affiliates={affiliates} level={item}/>)
            }
            </ul> : null
        }
    </>
}

const columns = [
    {
        title:'Date',
        dataIndex:'created_at',
        render:(value)=>{
            return <FormattedDate value={new Date(value)} year={"numeric"} day={"numeric"} month={"short"} />
        }
    },
    {
        title:'Order #',
        dataIndex:'number'
    },
    {
        title:'Amount',
        dataIndex: 'subtotal',
        type:'number',
        render:(value)=><CurrencyNumber value={value} />
    }
]




function RenderLevelSummary({affiliates, level}){
    const data = affiliates.filter((item)=>item.level === level);
    const revenue = Number(data.reduce((acc, cur)=> acc.plus(cur.revenue), Big(0)).toFixed(0))
    const numOrders = Number(data.reduce((acc, cur)=> acc.plus(cur.numOrders), Big(0)).toFixed(0))
    const earnings = Number(data.reduce((acc, cur)=> acc.plus(cur.earnings), Big(0)).toFixed(2))

    return <li className={'list-group-item'}>
        <div className="row">
            <div className="col-md-4">
                <div>Level {level}</div>
            </div>
            <div className="col-md-4 text-center">
                {
                    numOrders > 0 ?
                        <div>{numOrders} orders</div> : "-"
                }
            </div>
            <div className="col-md-4 text-right">
                {
                    revenue > 0 ?
                        <CurrencyNumber value={revenue}/> : "-"
                }
                {
                    earnings > 0 ?<small className={"text-muted pl-2"}>(<CurrencyNumber value={earnings} />)</small> : null
                }
            </div>
        </div>

    </li>
}

function CardItem({value, label}){
    return <div className="col-md-6">
        <div className="card">
            <div className="card-body">
                <h3>{value}</h3>
                <small><FormattedMessage id={label} /></small>
            </div>
        </div>
    </div>
}

function ShowDownlineSales({affiliate_id, startDate, endDate}){
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(false)
    useEffect(()=>{
        if(affiliate_id){
            setLoading(true)
            get('/mlm/downline_orders',{
                affiliate_id,
                startDate,
                endDate,
            }).then((data)=>{
                setLoading(false)
                setData(data.orders)
            })
        }
    },[affiliate_id, endDate, startDate])
    return <BootStrapTable dataSource={data} loading={loading} columns={columns} />

}

function RenderTree({affiliates, parent_id, startDate, endDate}){
    const [visible, setVisible] = useState({})
    const site_id = useSelector((state)=>state.settings.site_id)
    const website = useSelector((state)=>state.settings.website)
    const toggleVisibility = useCallback((affiliate_id)=>{
        return (e)=>{
            if(e){
                e.stopPropagation();
            }
//            if(site_id == 155413) {
                setVisible((v) => {
                    return {
                        ...v,
                        [affiliate_id]: !v[affiliate_id]
                    }
                })
//            }
        }
    },[site_id])
    const data = useMemo(()=>{
        return affiliates.filter((item)=>Number(item.parent_id) === Number(parent_id))
    },[affiliates])
    if(data.length === 0) {
        return null
    }
    const level = data[0].level;
    const color = level > 1 ? colorsArray[level-1] : undefined;
    return <ul className={`list-group ${level > 1 ? "mt-2" : ''}`}>
        {
            data.map(({name, email, numOrders, revenue, ref_code, profile_photo, affiliate_id, earnings})=>{
                    return <li className="list-group-item" onClick={toggleVisibility(affiliate_id)}
                               key={String(affiliate_id)}>
                        <div className="row">
                            <div className="col d-flex align-items-center">
                                <div className={"mr-2"}>
                                    {
                                        profile_photo ?
                                            <img src={profile_photo}
                                                 className={"shadow-sm"}
                                                 style={{
                                                borderRadius: 32,
                                                width: 48,
                                                height: 48,
                                                overflow: 'hidden',
                                                objectFit: 'cover'
                                            }}/> : <div
                                                className={"shadow-sm d-flex align-items-center justify-content-center"}
                                                style={{
                                                fontSize:20,
                                                fontWeight:300,
                                                backgroundColor:'#34495e',
                                                color:'#efefef',
                                                textTransform:"uppercase",
                                                borderRadius: 32,
                                                width: 48,
                                                height: 48,
                                                overflow: 'hidden',
                                                objectFit: 'cover'
                                            }}>{name.substring(0, 2)}</div>
                                    }
                                </div>
                                <div>
                                    <div title={'Level '+level} style={{color}}>{name} <a className="ml-1" style={{fontSize:12}} href={buildRefLink(ref_code, website)}><ExternalLinkIcon /></a></div>
                                    <small className={"text-muted downline_email"}><a rel={"noreferrer noopener"} href={"mailto:"+email} target={"_blank"}>{email}</a></small>
                                </div>
                            </div>
                            {
                                numOrders > 0 || revenue > 0 || earnings > 0 ?
                                    <div className="col text-right">
                                        <small><div><FormattedNumber value={numOrders}/> orders </div></small>
                                        <span className={ revenue > 0 ? "text-success" : ""}>
                                            <CurrencyNumber value={revenue}/>
                                        </span>
                                        {
                                            earnings > 0 ?
                                                <small className={"text-muted d-none pl-2"}>(<CurrencyNumber value={earnings}/>)</small>
                                                : null
                                        }
                                    </div> : null
                            }
                        </div>
                        {
                            visible[affiliate_id] ? <div className={"py-2"}>
                                <ShowDownlineSales
                                    startDate={startDate} endDate={endDate}
                                    affiliate_id={affiliate_id} />
                                </div>
                                : null
                        }
                        <RenderTree
                            startDate={startDate}
                            endDate={endDate}
                            parent_id={affiliate_id} affiliates={affiliates} />
                    </li>
                })
        }
    </ul>
}

function getUplines(data, parent_id){
    if(!parent_id) return []
    const parent = data.find((i)=>i.affiliate_id == parent_id)
    if(!parent) return [];
    return [parent, ...getUplines(data, parent.parent_id)]
}

function RenderAffiliateSales({affiliates, startDate, endDate, affiliate_id}){
    const [showActive, setShowActive] = useState(true);
    const [keyword, setKeyword] = useState('')
    const _keyword = useDebounce(keyword, 300);
    const filteredAffiliates = useMemo(()=>{
        console.log('calling filtered affiliates')
        let a = affiliates;
        if(_keyword || showActive) {
            if (_keyword) {
                const k = _keyword.toLowerCase()
                a = a.filter(
                    ({name, email}) => name.toLowerCase().match(k) ||
                        email.toLowerCase().match(k))
            }
            if (showActive) {
                a = a.filter(({revenue, numOrders})=> Number(revenue) || Number(numOrders))
            }
            // make sure their top heirarchies is also preserved
            for (let {parent_id, affiliate_id} of a) {
                if (!a.find((i) => i.affiliate_id == parent_id)) {
                    // we need to add the top lines
                    const parents = getUplines(affiliates, parent_id)
                    for (let p of parents) {
                        a.push(p);
                    }
                }
            }
            return uniqby(a, ({affiliate_id})=>affiliate_id).sort((a, b)=>b.revenue+b.numOrders - a.revenue+a.numOrders);
        }else {
            return affiliates.sort((a, b)=>b.revenue+b.numOrders - a.revenue+a.numOrders)
        }
    },[_keyword, showActive])

    return <>
        <div className="row align-items-center">
                <div className="col-md-3 mb-2">
                    <h4><FormattedMessage id={"Details"}/></h4>
                </div>
                <div className="col-md-6 mb-2">
                    <Input value={keyword} onChange={setKeyword} type={"search"} placeholder={"Search..."} />
                </div>
                <div className="col-md-3 mb-2">
                    <Checkbox onChange={(e)=>setShowActive(e.target.checked)} label={<FormattedMessage id={"Show active affiliates"} />} checked={showActive} />
                </div>
            </div>
            <RenderTree  startDate={startDate} endDate={endDate}
                         affiliates={filteredAffiliates} parent_id={affiliate_id}/>
    </>
}
function mapStateToProps(state){
    const {mlm} = state.commissions;
    const {affiliate_id} = state.settings
    return {
        num_levels: (mlm && mlm.standard && mlm.standard.length) || 0,
        affiliate_id,
    }
}


export default connect(mapStateToProps)(TeamExplorer);
